// Reajuste em lote (o "Atualizar em Lote + Gerar Aditivos" do Apps Script): lê o CSV/Excel do RH
// (CNPJ, NOVO VALOR, INICIO NOVO VALOR, MOTIVO), mostra a prévia validada pelo backend, grava os
// reajustes e gera os aditivos um a um — cada aditivo é uma requisição, então não há limite de tempo.
import { useState } from "react";
import * as XLSX from "xlsx";
import { FileDown, FileUp } from "lucide-react";
import { postRhGerarAditivo, postRhReajusteLote } from "../../api";
import { formatData } from "../estoque/comum";
import { AvisoErro, Modal, btnStyle, formatBRL } from "../estoque/ui";

const COLUNAS = { cnpj: "cnpj", novovalor: "novo_valor", inicionovovalor: "data_vigencia", motivo: "motivo" };
const chave = (cabecalho) => String(cabecalho).toLowerCase().normalize("NFD").replace(/[^a-z]/g, "");

/** Data vinda do Excel como número de série → "aaaa-mm-dd"; o resto segue como texto. */
function celula(valor, campo) {
  if (campo === "data_vigencia" && typeof valor === "number") {
    // Arredonda: datas gravadas com fuso chegam como 46081.9996 (= 01/03, não 28/02)
    const d = XLSX.SSF.parse_date_code(Math.round(valor));
    return `${d.y}-${String(d.m).padStart(2, "0")}-${String(d.d).padStart(2, "0")}`;
  }
  return valor === null || valor === undefined ? "" : String(valor).trim();
}

async function lerArquivo(arquivo) {
  // CSV como texto puro: o leitor do XLSX interpretaria "01/03/2026" como data americana
  const planilha = arquivo.name.toLowerCase().endsWith(".csv")
    ? XLSX.read(await arquivo.text(), { type: "string", raw: true })
    : XLSX.read(await arquivo.arrayBuffer(), { type: "array" });
  const linhas = XLSX.utils.sheet_to_json(planilha.Sheets[planilha.SheetNames[0]], { raw: true, defval: "" });
  if (!linhas.length) throw new Error("O arquivo não tem linhas.");
  const mapa = {};
  Object.keys(linhas[0]).forEach((c) => { if (COLUNAS[chave(c)]) mapa[c] = COLUNAS[chave(c)]; });
  const faltando = ["cnpj", "novo_valor", "data_vigencia"].filter((c) => !Object.values(mapa).includes(c));
  if (faltando.length) throw new Error("O arquivo precisa das colunas CNPJ, NOVO VALOR e INICIO NOVO VALOR.");
  return linhas
    .map((l, i) => {
      const item = { linha: i + 2 };
      Object.entries(mapa).forEach(([coluna, campo]) => { item[campo] = celula(l[coluna], campo); });
      return item;
    })
    .filter((l) => l.cnpj);
}

function baixarModelo() {
  const csv = "CNPJ;NOVO VALOR;INICIO NOVO VALOR;MOTIVO\n00.000.000/0000-00;5.500,00;01/03/2026;Reajuste anual\n";
  const url = URL.createObjectURL(new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = "modelo_reajuste_lote.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export default function LoteReajusteModal({ C, documentosDisponiveis, onClose, onAlterado }) {
  const [linhas, setLinhas] = useState(null);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState("");
  const [ocupado, setOcupado] = useState(false);
  const [geracao, setGeracao] = useState(null); // { feitos, total, erros: [] }
  // Modo correção (o do Apps Script): corrige o valor de reajustes já lançados, achados por CNPJ + data
  const [corrigir, setCorrigir] = useState(false);
  const [lixeira, setLixeira] = useState(false);

  async function previa(lidas, modoCorrecao) {
    setErro("");
    setResultado(null);
    setGeracao(null);
    setOcupado(true);
    try {
      setResultado(await postRhReajusteLote(lidas, false, { corrigir: modoCorrecao }));
    } catch (er) {
      setErro(er.message);
    } finally {
      setOcupado(false);
    }
  }

  async function escolher(e) {
    const arquivo = e.target.files[0];
    e.target.value = "";
    if (!arquivo) return;
    try {
      const lidas = await lerArquivo(arquivo);
      setLinhas(lidas);
      await previa(lidas, corrigir);
    } catch (er) {
      setErro(er.message);
    }
  }

  function trocarModo(modoCorrecao) {
    setCorrigir(modoCorrecao);
    setLixeira(false);
    if (linhas) previa(linhas, modoCorrecao);
  }

  async function aplicar() {
    setOcupado(true);
    setErro("");
    try {
      const r = await postRhReajusteLote(linhas, true, { corrigir, lixeira: corrigir && lixeira });
      setResultado(r);
      onAlterado();
      // Reajuste antigo sem nº de aditivo (histórico da planilha) é corrigido, mas não tem aditivo para gerar
      if (documentosDisponiveis) await gerarAditivos(r.linhas.filter((l) => l.movimentacao_id && (!corrigir || l.numero_aditivo)));
    } catch (er) {
      setErro(er.message);
    } finally {
      setOcupado(false);
    }
  }

  async function gerarAditivos(criadas) {
    const estado = { feitos: 0, total: criadas.length, erros: [] };
    setGeracao({ ...estado });
    for (const l of criadas) {
      try {
        const doc = await postRhGerarAditivo(l.movimentacao_id);
        l.documento = doc;
      } catch (er) {
        estado.erros.push(`${l.nome}: ${er.message}`);
      }
      estado.feitos += 1;
      setGeracao({ ...estado });
    }
    onAlterado();
  }

  const aplicado = resultado?.aplicado;
  const cor = { ok: C.success, ignorado: C.warning, erro: C.danger };
  const rotulo = { ok: aplicado ? (corrigir ? "Corrigido" : "Registrado") : "Pronto", ignorado: "Ignorado", erro: "Erro" };
  const modoBtn = (ativo) => ({ ...btnStyle(ativo ? C.brand : C.muted, !ativo), opacity: ocupado ? 0.6 : 1 });
  const th = { textAlign: "left", padding: "6px 8px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", color: C.muted, borderBottom: `1px solid ${C.border}` };
  const td = { padding: "6px 8px", fontSize: 12, borderBottom: `1px solid ${C.border}` };

  return (
    <Modal C={C} titulo="Reajuste em lote" onClose={onClose} largura={900}
      rodape={<>
        <button onClick={onClose} style={btnStyle(C.muted, true)}>{aplicado ? "Fechar" : "Cancelar"}</button>
        {resultado && !aplicado && (
          <button onClick={aplicar} disabled={ocupado || !resultado.totais.ok} style={{ ...btnStyle(C.brand), opacity: ocupado || !resultado.totais.ok ? 0.5 : 1 }}>
            {corrigir ? "Corrigir" : "Registrar"} {resultado.totais.ok} reajuste(s){documentosDisponiveis ? " e gerar aditivos" : ""}
          </button>
        )}
      </>}>
      <AvisoErro C={C} onClose={() => setErro("")}>{erro}</AvisoErro>
      <p style={{ fontSize: 13, margin: "0 0 12px" }}>
        Colunas: <strong>CNPJ</strong>, <strong>NOVO VALOR</strong>, <strong>INICIO NOVO VALOR</strong> e <strong>MOTIVO</strong> (opcional).
        {corrigir
          ? " Cada linha corrige o reajuste já lançado para aquele CNPJ na mesma data de início, e o aditivo é gerado de novo."
          : " O nº do aditivo é sempre o próximo de cada contrato. Reajuste já existente na mesma data é ignorado."}
      </p>
      {!aplicado && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => trocarModo(false)} disabled={ocupado} aria-pressed={!corrigir} style={modoBtn(!corrigir)}>Novos reajustes</button>
            <button onClick={() => trocarModo(true)} disabled={ocupado} aria-pressed={corrigir} style={modoBtn(corrigir)}>Corrigir lançamentos existentes</button>
          </div>
          {corrigir && (
            <label style={{ display: "flex", gap: 8, alignItems: "flex-start", fontSize: 13, marginTop: 10 }}>
              <input type="checkbox" checked={lixeira} onChange={(e) => setLixeira(e.target.checked)} style={{ marginTop: 3 }} />
              <span>
                Mandar os aditivos antigos para a lixeira do Drive
                <span style={{ display: "block", fontSize: 12, color: C.muted }}>
                  Desmarcado, os aditivos antigos continuam no Drive e na lista do contrato, ao lado dos novos.
                </span>
              </span>
            </label>
          )}
        </div>
      )}
      {!documentosDisponiveis && (
        <p style={{ fontSize: 12, color: C.warning, margin: "0 0 12px" }}>Os modelos do Drive ainda não estão acessíveis: os reajustes serão registrados sem aditivo (gere depois em cada contrato).</p>
      )}
      {!aplicado && (
        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
          <label style={{ ...btnStyle(C.brand), opacity: ocupado ? 0.6 : 1 }}>
            <FileUp size={15} /> {ocupado ? "Lendo…" : "Escolher CSV ou Excel"}
            <input type="file" accept=".csv,.xlsx,.xls" onChange={escolher} disabled={ocupado} style={{ display: "none" }} />
          </label>
          <button onClick={baixarModelo} style={btnStyle(C.muted, true)}><FileDown size={15} /> Baixar modelo</button>
        </div>
      )}

      {geracao && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Gerando aditivos: {geracao.feitos} de {geracao.total}</div>
          <div style={{ height: 8, background: C.border, borderRadius: 999, marginTop: 6, overflow: "hidden" }}>
            <div style={{ width: `${geracao.total ? (100 * geracao.feitos) / geracao.total : 100}%`, height: "100%", background: C.brand, transition: "width 0.3s" }} />
          </div>
          {geracao.erros.map((e) => <div key={e} style={{ fontSize: 12, color: C.danger, marginTop: 4 }}>{e}</div>)}
        </div>
      )}

      {resultado && (
        <>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 13, marginBottom: 8 }}>
            {Object.entries(resultado.totais).map(([s, n]) => <span key={s} style={{ color: cor[s], fontWeight: 700 }}>{rotulo[s]}: {n}</span>)}
            {aplicado && resultado.documentos_na_lixeira > 0 && <span style={{ color: C.muted }}>{resultado.documentos_na_lixeira} aditivo(s) antigo(s) na lixeira</span>}
            {aplicado && resultado.documentos_mantidos > 0 && <span style={{ color: C.warning }}>{resultado.documentos_mantidos} aditivo(s) antigo(s) não foram para a lixeira (continuam listados)</span>}
          </div>
          <div style={{ maxHeight: "45vh", overflow: "auto", border: `1px solid ${C.border}`, borderRadius: 8 }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><th style={th}>Linha</th><th style={th}>PJ</th><th style={th}>Vigência</th><th style={{ ...th, textAlign: "right" }}>Novo valor</th><th style={th}>Situação</th></tr></thead>
              <tbody>
                {resultado.linhas.map((l) => (
                  <tr key={l.linha}>
                    <td style={td}>{l.linha}</td>
                    <td style={td}>{l.nome || l.cnpj}</td>
                    <td style={td}>{l.data_vigencia ? formatData(l.data_vigencia) : "—"}</td>
                    <td style={{ ...td, textAlign: "right", whiteSpace: "nowrap" }}>
                      {l.valor_anterior != null && <span style={{ color: C.muted }}>{formatBRL(l.valor_anterior)} → </span>}
                      {l.valor ? formatBRL(l.valor) : "—"}
                    </td>
                    <td style={{ ...td, color: cor[l.status] }}>
                      {rotulo[l.status]}{l.mensagem && ` — ${l.mensagem}`}
                      {l.documento && <> · <a href={l.documento.url} target="_blank" rel="noreferrer" style={{ color: C.brand }}>aditivo</a></>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  );
}
